import type {
  CargaTrabalhoEtapas,
  CargaTrabalhoIntervalo,
  CargaTrabalhoObra,
} from "@/modules/carga-trabalho";

/**
 * Modo Simulação da Carga de Trabalho (HUs 2220, 2225 e 2224). Estado só do cliente: nada
 * é gravado até o usuário aplicar (HU 2221). Um único cenário vale para as três
 * perspectivas; cada alteração guarda o que mostrar (anterior × novo) e como será
 * reproduzida na API (`acao` + `payloadReplay`).
 */

/** Obra afetada por uma alteração, como as perspectivas a identificam. */
export interface ObraSimulada {
  id: string;
  codigo: string;
  nome: string;
  situacao: string;
  embarcacao?: { nome?: string | null; acronimo?: string | null } | null;
  /** Etapas da obra, quando a perspectiva as tem (a alocação vem de `CargaTrabalhoObra`). */
  etapas?: CargaTrabalhoEtapas;
  /** Período da etapa Obra (`AAAA-MM-DD`), quando a perspectiva o traz sem etapas (Fornecedor). */
  obraInicio?: string;
  obraFim?: string;
}

/**
 * Etapa Obra como estava quando a alteração foi capturada: a fotografia que o Aplicar
 * compara com as datas que o core devolve (RN021). `null` quando a perspectiva não a traz (Local).
 */
export function etapaObraNaCaptura(obra: ObraSimulada): CargaTrabalhoIntervalo | null {
  if (obra.etapas?.obra) return obra.etapas.obra;
  return obra.obraInicio && obra.obraFim ? { inicio: obra.obraInicio, fim: obra.obraFim } : null;
}

/** Fornecedor ou Local, antes ou depois da alteração. */
export interface RecursoSimulado {
  id: string;
  codigo: string;
  nome: string;
}

/** Profissional antes ou depois de um remanejamento. */
export interface ProfissionalSimulado {
  id: string;
  nome: string;
  time: string | null;
}

/** Um profissional na equipe da obra (o que o Definir Equipe monta). */
export interface MembroEquipeSimulado {
  usuarioId: string;
  nome: string;
  time: string | null;
  papelObraId: string;
  /** Nome do papel, para os cartões do Aplicar Simulação. */
  papel?: string;
  inicio: string;
  fim: string;
  /** Identificador da alocação gravada, quando a linha edita um período (HU 2051). */
  alocacaoId?: string;
}

interface AlteracaoBase {
  /**
   * RN011: justificativa única dos conflitos novos que a alteração cria, informada ao
   * incluí-la; vai na gravação (HU 2221) no lugar da justificativa geral.
   */
  justificativaConflitos?: string;
  /**
   * Conflitos novos que a alteração deixa em aberto, por nome do recurso (os da justificativa
   * acima): o Aplicar os mostra como "continua com N conflito" (Figma 9651:41026, D-040).
   */
  conflitosResiduais?: Readonly<Record<string, number>>;
  /**
   * Voltou de um Aplicar Simulação sem ser gravada ("Ajustar na simulação", HU 2221): o
   * motivo, para identificá-la como ajuste pendente.
   */
  ajustePendente?: string;
}

/** Definir Equipe (HU 2050) → `POST .../obras/{obraId}/profissionais`. */
export interface AlteracaoEquipe extends AlteracaoBase {
  acao: "definir-equipe";
  /** A obra inteira (etapas, embarcação), para desenhar as barras novas sem consultar a API. */
  obra: CargaTrabalhoObra;
  /** Período da etapa Obra: base da disponibilidade (RN14). */
  periodo: CargaTrabalhoIntervalo;
  anterior: MembroEquipeSimulado[];
  novo: MembroEquipeSimulado[];
  /**
   * Justificativa da Revisão de um remanejamento que caiu nesta equipe simulada (obrigatória,
   * até 500); a gravação a envia no POST de definir equipe, antes da do RN011 e da geral.
   */
  justificativa?: string;
}

/** Remanejar Profissional (HU 2040) → `PATCH .../profissionais/{alocacaoId}/remanejamento`. */
export interface AlteracaoRemanejarProfissional extends AlteracaoBase {
  acao: "remanejar-profissional";
  obra: ObraSimulada;
  alocacaoId: string;
  periodo: CargaTrabalhoIntervalo;
  anterior: ProfissionalSimulado;
  novo: ProfissionalSimulado;
  /** Papel da alocação (nome, de `listAllPapeisObra` pelo `papelObraId`), para o tooltip e o cartão. */
  papel?: string;
  /**
   * Justificativa digitada na Revisão do Remanejar (Figma 9497:109954, obrigatória, até 500);
   * a gravação (HU 2221) a envia no PATCH de remanejamento.
   */
  justificativa?: string;
}

/** Remanejar Fornecedor (HU 2041) → `PATCH .../fornecedores/{alocacaoId}/remanejamento`. */
export interface AlteracaoRemanejarFornecedor extends AlteracaoBase {
  acao: "remanejar-fornecedor";
  obra: ObraSimulada;
  alocacaoId: string;
  periodo: CargaTrabalhoIntervalo;
  anterior: RecursoSimulado;
  novo: RecursoSimulado;
  /** Nome do Escopo Macro escolhido no diálogo (só exibição: não vai ao backend), para os cartões. */
  escopoMacro?: string;
  /**
   * Justificativa digitada na Revisão do Remanejar (Figma 9497:109954, obrigatória, até 500);
   * a gravação (HU 2221) a envia no PATCH de remanejamento.
   */
  justificativa?: string;
}

/** Remanejar Local (HU 2042) → `PATCH .../locais/{alocacaoId}/remanejamento`. */
export interface AlteracaoRemanejarLocal extends AlteracaoBase {
  acao: "remanejar-local";
  obra: ObraSimulada;
  alocacaoId: string;
  periodo: CargaTrabalhoIntervalo;
  anterior: RecursoSimulado;
  novo: RecursoSimulado;
  /**
   * Justificativa digitada na Revisão do Remanejar (Figma 9497:109954, obrigatória, até 500);
   * a gravação (HU 2221) a envia no PATCH de remanejamento.
   */
  justificativa?: string;
}

/** Conflito marcado como tratado: a sobreposição continua, em verde, fora do selo. */
interface AlteracaoIgnorarBase extends AlteracaoBase {
  /** As obras do conflito, para a marca e a descrição. */
  obras: ObraSimulada[];
  alocacaoIds: string[];
  /** Trecho sobreposto (`YYYY-MM-DD`). */
  conflito: CargaTrabalhoIntervalo;
  /**
   * Justificativa da ação, pedida no modal de Ignorar (obrigatória, até 500; comentários da
   * 2224 e da 2221). A gravação a envia; sem ela, usa a geral do Aplicar.
   */
  justificativa?: string;
}

/** Ignorar Conflito de Profissional → `POST .../profissionais/conflitos-ignorados`. */
export interface AlteracaoIgnorarProfissional extends AlteracaoIgnorarBase {
  acao: "ignorar-conflito-profissional";
  profissional: ProfissionalSimulado;
}

/** Ignorar Conflito de Fornecedor → `POST .../fornecedores/conflitos-ignorados`. */
export interface AlteracaoIgnorarFornecedor extends AlteracaoIgnorarBase {
  acao: "ignorar-conflito-fornecedor";
  fornecedor: RecursoSimulado;
}

/** Ignorar Conflito de Local → `POST .../locais/conflitos-ignorados`. */
export interface AlteracaoIgnorarLocal extends AlteracaoIgnorarBase {
  acao: "ignorar-conflito-local";
  local: RecursoSimulado;
}

export type AlteracaoSimulada =
  | AlteracaoEquipe
  | AlteracaoRemanejarProfissional
  | AlteracaoRemanejarFornecedor
  | AlteracaoRemanejarLocal
  | AlteracaoIgnorarProfissional
  | AlteracaoIgnorarFornecedor
  | AlteracaoIgnorarLocal;

export type AcaoSimulada = AlteracaoSimulada["acao"];

export type AlteracaoDaAcao<A extends AcaoSimulada> = Extract<AlteracaoSimulada, { acao: A }>;

/** Cenário montado durante a simulação, na ordem em que as alterações foram feitas. */
export interface CenarioSimulacao {
  alteracoes: readonly AlteracaoSimulada[];
}

export type SimulacaoState = { ativo: false } | { ativo: true; cenario: CenarioSimulacao };

export type SimulacaoAction =
  | { type: "entrar" }
  | { type: "sair" }
  | { type: "incluir"; alteracao: AlteracaoSimulada }
  | { type: "remover"; chave: string }
  /** "Ajustar na simulação" (HU 2221): o cenário passa a ser só o que não foi aplicado. */
  | { type: "redefinir"; alteracoes: readonly AlteracaoSimulada[] };

export const SIMULACAO_INATIVA: SimulacaoState = { ativo: false };

export const CENARIO_VAZIO: CenarioSimulacao = { alteracoes: [] };

/** Alocação de um conflito novo, para o modal de justificativa (RN011). */
export interface ConflitoNovoItem {
  /** "Obra 24 - Nome" ou "Ausência programada". */
  rotulo: string;
  /** Código da obra, quando o item é uma obra (não a ausência programada). */
  obraCodigo?: string;
  /** Id da alocação, para o modal saber qual delas a alteração criou (a obra pode ser a mesma). */
  alocacaoId?: string;
  inicio: string;
  fim: string;
}

/** Conflito que a alteração criaria: o recurso afetado e as duas ocupações sobrepostas. */
export interface ConflitoNovo {
  recurso: string;
  itens: [ConflitoNovoItem, ConflitoNovoItem];
}

/** O que uma perspectiva entrega para incluir uma alteração (com o RN011, se houver). */
export interface InclusaoSimulada {
  alteracao: AlteracaoSimulada;
  conflitosNovos: ConflitoNovo[];
}

export function alteracoesDaAcao<A extends AcaoSimulada>(
  cenario: CenarioSimulacao,
  acao: A,
): AlteracaoDaAcao<A>[] {
  return cenario.alteracoes.filter(
    (alteracao): alteracao is AlteracaoDaAcao<A> => alteracao.acao === acao,
  );
}

function recursoDoIgnorado(
  alteracao: AlteracaoIgnorarProfissional | AlteracaoIgnorarFornecedor | AlteracaoIgnorarLocal,
): { id: string; nome: string } {
  switch (alteracao.acao) {
    case "ignorar-conflito-profissional":
      return alteracao.profissional;
    case "ignorar-conflito-fornecedor":
      return alteracao.fornecedor;
    case "ignorar-conflito-local":
      return alteracao.local;
  }
}

/**
 * Identidade da alteração: o recurso alterado. Alterar de novo o mesmo recurso substitui
 * a alteração anterior em vez de somar outra.
 */
export function chaveAlteracao(alteracao: AlteracaoSimulada): string {
  switch (alteracao.acao) {
    case "definir-equipe":
      return `${alteracao.acao}:${alteracao.obra.id}`;
    case "remanejar-profissional":
    case "remanejar-fornecedor":
    case "remanejar-local":
      return `${alteracao.acao}:${alteracao.alocacaoId}`;
    case "ignorar-conflito-profissional":
    case "ignorar-conflito-fornecedor":
    case "ignorar-conflito-local":
      return `${alteracao.acao}:${recursoDoIgnorado(alteracao).id}:${[...alteracao.alocacaoIds].sort().join("|")}`;
  }
}

export interface DiffEquipe {
  added: MembroEquipeSimulado[];
  removed: MembroEquipeSimulado[];
  changed: MembroEquipeSimulado[];
  unchanged: MembroEquipeSimulado[];
}

function mesmoPapelEPeriodo(a: MembroEquipeSimulado, b: MembroEquipeSimulado): boolean {
  return a.papelObraId === b.papelObraId && a.inicio === b.inicio && a.fim === b.fim;
}

export interface ParEquipe {
  membro: MembroEquipeSimulado;
  previa: MembroEquipeSimulado | undefined;
  /** Posição de `membro` em `novo`: o mesmo profissional em duas linhas se distingue por ela. */
  index: number;
}

/**
 * Casa cada membro de `novo` com o seu anterior: pela alocação gravada quando há e, no resto, pelo
 * profissional (o primeiro ainda livre). O mesmo profissional em duas linhas não se confunde.
 */
export function parearEquipe(
  anterior: readonly MembroEquipeSimulado[],
  novo: readonly MembroEquipeSimulado[],
): { pares: ParEquipe[]; removed: MembroEquipeSimulado[] } {
  const porAlocacao = new Map(
    anterior.flatMap((membro) => (membro.alocacaoId ? [[membro.alocacaoId, membro] as const] : [])),
  );
  const usados = new Set<MembroEquipeSimulado>();
  const previas = novo.map((membro) => {
    const previa = membro.alocacaoId ? porAlocacao.get(membro.alocacaoId) : undefined;
    if (previa) usados.add(previa);
    return previa;
  });
  const pares = novo.map((membro, index): ParEquipe => {
    const previa =
      previas[index] ??
      anterior.find((antes) => !usados.has(antes) && antes.usuarioId === membro.usuarioId);
    if (previa) usados.add(previa);
    return { membro, previa, index };
  });
  return { pares, removed: anterior.filter((antes) => !usados.has(antes)) };
}

/** Quem de fato mudou na equipe: o colega intacto fica de fora do cenário. */
export function diffEquipe(
  anterior: readonly MembroEquipeSimulado[],
  novo: readonly MembroEquipeSimulado[],
): DiffEquipe {
  const { pares, removed } = parearEquipe(anterior, novo);
  const added: MembroEquipeSimulado[] = [];
  const changed: MembroEquipeSimulado[] = [];
  const unchanged: MembroEquipeSimulado[] = [];

  for (const { membro, previa } of pares) {
    if (!previa) added.push(membro);
    else if (mesmoPapelEPeriodo(previa, membro)) unchanged.push(membro);
    else changed.push(membro);
  }

  return { added, changed, unchanged, removed };
}

/** Pares que entram ou mudam papel/período, na ordem de `novo`, cada um com o seu anterior. */
export function paresComEfeito(
  anterior: readonly MembroEquipeSimulado[],
  novo: readonly MembroEquipeSimulado[],
): ParEquipe[] {
  return parearEquipe(anterior, novo).pares.filter(
    ({ membro, previa }) => !previa || !mesmoPapelEPeriodo(previa, membro),
  );
}

/** Membros que entram ou mudam papel/período, na ordem de `novo`. */
export function membrosComEfeito(
  anterior: readonly MembroEquipeSimulado[],
  novo: readonly MembroEquipeSimulado[],
): MembroEquipeSimulado[] {
  return paresComEfeito(anterior, novo).map(({ membro }) => membro);
}

export function composicaoEquipeMudou(diff: DiffEquipe): boolean {
  return diff.added.length > 0 || diff.removed.length > 0;
}

/** Só datas mudaram: mesmo conjunto de pessoas e o mesmo papel em cada uma. */
export function soPeriodoDaEquipe(
  anterior: readonly MembroEquipeSimulado[],
  novo: readonly MembroEquipeSimulado[],
): boolean {
  const { pares, removed } = parearEquipe(anterior, novo);
  if (removed.length > 0 || pares.some(({ previa }) => !previa)) return false;
  return pares.every(({ membro, previa }) => previa?.papelObraId === membro.papelObraId);
}

function mesmaEquipe(a: MembroEquipeSimulado[], b: MembroEquipeSimulado[]): boolean {
  const diff = diffEquipe(a, b);
  return !composicaoEquipeMudou(diff) && diff.changed.length === 0;
}

/** Alteração que deixa o recurso como já estava: não entra no cenário. */
export function isSemEfeito(alteracao: AlteracaoSimulada): boolean {
  switch (alteracao.acao) {
    case "definir-equipe":
      return mesmaEquipe(alteracao.anterior, alteracao.novo);
    case "remanejar-profissional":
    case "remanejar-fornecedor":
    case "remanejar-local":
      return alteracao.anterior.id === alteracao.novo.id;
    case "ignorar-conflito-profissional":
    case "ignorar-conflito-fornecedor":
    case "ignorar-conflito-local":
      return false;
  }
}

/** Mesma ação implica o mesmo tipo de alteração (é o discriminante da união). */
function mesmaAcao<T extends AlteracaoSimulada>(
  alteracao: T,
  outra: AlteracaoSimulada | undefined,
): outra is T {
  return outra?.acao === alteracao.acao;
}

/** O valor anterior de um recurso já alterado continua sendo o original. */
function comAnteriorDe<T extends Extract<AlteracaoSimulada, { anterior: unknown }>>(
  alteracao: T,
  existente: AlteracaoSimulada | undefined,
): T {
  return mesmaAcao(alteracao, existente)
    ? { ...alteracao, anterior: existente.anterior }
    : alteracao;
}

function manterAnteriorOriginal(
  alteracao: AlteracaoSimulada,
  existente: AlteracaoSimulada | undefined,
): AlteracaoSimulada {
  switch (alteracao.acao) {
    case "definir-equipe":
    case "remanejar-profissional":
    case "remanejar-fornecedor":
    case "remanejar-local":
      return comAnteriorDe(alteracao, existente);
    case "ignorar-conflito-profissional":
    case "ignorar-conflito-fornecedor":
    case "ignorar-conflito-local":
      return alteracao;
  }
}

/**
 * Inclui a alteração no cenário. Sobre um recurso já alterado, vale o valor novo e o
 * anterior continua sendo o original; se volta ao original, a alteração sai do cenário.
 */
export function incluirAlteracao(
  cenario: CenarioSimulacao,
  alteracao: AlteracaoSimulada,
): CenarioSimulacao {
  const chave = chaveAlteracao(alteracao);
  const existente = cenario.alteracoes.find((item) => chaveAlteracao(item) === chave);
  const combinada = manterAnteriorOriginal(alteracao, existente);
  const demais = cenario.alteracoes.filter((item) => chaveAlteracao(item) !== chave);

  return { alteracoes: isSemEfeito(combinada) ? demais : [...demais, combinada] };
}

export function removerAlteracao(cenario: CenarioSimulacao, chave: string): CenarioSimulacao {
  return { alteracoes: cenario.alteracoes.filter((item) => chaveAlteracao(item) !== chave) };
}

export function simulacaoReducer(state: SimulacaoState, action: SimulacaoAction): SimulacaoState {
  switch (action.type) {
    case "entrar":
      // Entrar de novo não descarta um cenário em andamento.
      return state.ativo ? state : { ativo: true, cenario: CENARIO_VAZIO };
    case "sair":
      return SIMULACAO_INATIVA;
    case "incluir":
      return state.ativo
        ? { ativo: true, cenario: incluirAlteracao(state.cenario, action.alteracao) }
        : state;
    case "remover":
      return state.ativo
        ? { ativo: true, cenario: removerAlteracao(state.cenario, action.chave) }
        : state;
    case "redefinir":
      return state.ativo ? { ativo: true, cenario: { alteracoes: action.alteracoes } } : state;
  }
}
